import React from 'react';

import { AddContactForm } from './form';
import { AddContactInputs } from './contact_inputs';
import { Button } from '@buildoutinc/blueprint-react/ui/Button';
import { ContactRoleInput } from './role_input';
import { ErrorsAlert } from 'components/ui/alert/errors';
import { ForRecordInput } from './for_record_input';
import { Modal } from 'components/ui/modal';
import { VisibilityInput } from './visibility_input';
import { useAddContactWorkflow } from './workflow';

import type { ForRecordOptions, NewContactRole } from './types';
import type { Role, Visibility } from 'bundles/backoffice/contacts/types';

interface Props {
  allowTagging?: boolean;
  contactLabel?: string;
  forRecordOptions?: ForRecordOptions;
  roleOptions?: Role[];
  show: boolean;
  showQuickbooks?: boolean;
  visibilityOptions?: Visibility[];
  onAdd: (contactRole: NewContactRole) => unknown;
  onClose: () => void;
}

export function AddContactModal({
  allowTagging,
  contactLabel = 'contact',
  forRecordOptions,
  roleOptions,
  show,
  showQuickbooks,
  visibilityOptions,
  onAdd,
  onClose,
}: Props) {
  const workflow = useAddContactWorkflow({ forRecordOptions, roleOptions, visibilityOptions, onComplete: onAdd });

  async function save() {
    if (await workflow.save()) {
      close();
    }
  }

  function close() {
    workflow.reset();
    onClose();
  }

  return (
    <Modal open={show} onClose={close}>
      <Modal.Content centered>
        <Modal.Header>
          <Modal.Title>Add {contactLabel}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <ErrorsAlert className="mb-3" errors={workflow.errors} />

          <AddContactForm workflow={workflow}>
            <AddContactInputs allowTagging={allowTagging} showQuickbooks={showQuickbooks} workflow={workflow} />
            <ContactRoleInput workflow={workflow} />
            <VisibilityInput workflow={workflow} />
            <ForRecordInput workflow={workflow} />
          </AddContactForm>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button disabled={!workflow.canSave} onClick={save}>
            {workflow.step === 'form' ? `Add ${contactLabel}` : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
